import { buildLocationProfilePath } from "@/app/locations/routes";

import { VENUE_RESULTS_FIXTURES } from "../../results/resultsFixtures";
import {
  CUSTOMER_FAVOURITES_SCHEMA_VERSION,
  type CustomerFavouriteLocationCatalog,
  type CustomerFavouriteLocationCatalogEntry,
  type CustomerFavouriteLocationReferenceV1,
  type CustomerFavouritesCapabilities,
  type CustomerFavouritesLocale,
  type CustomerFavouritesSnapshotV1,
} from "./customerFavouritesContracts";
import {
  joinCustomerFavouriteLocations,
  validateCustomerFavouriteLocationCatalog,
  validateCustomerFavouritesSnapshot,
} from "./customerFavouritesValidation";

export const CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_ID =
  "frontend-fixture-customer-favourites";
export const CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_REVISION =
  "frontend-fixture-customer-favourites-r1";
export const CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION =
  "frontend-fixture-location-display-r1";
export const CUSTOMER_FAVOURITES_FIXTURE_SNAPSHOT_REVISION =
  "frontend-fixture-favourites-snapshot-r1";
export const CUSTOMER_FAVOURITES_FIXTURE_STALE_SNAPSHOT_REVISION =
  "frontend-fixture-favourites-snapshot-r0";
export const CUSTOMER_FAVOURITES_FIXTURE_DEFAULT_PAGE_SIZE = 4;
export const CUSTOMER_FAVOURITES_FIXTURE_DEFAULT_LOCALE = "en" as const;
export const CUSTOMER_FAVOURITES_FIXTURE_GENERATED_AT_EPOCH_MS = Date.UTC(
  2026,
  6,
  31,
  8,
  0,
  0,
);

export const CUSTOMER_FAVOURITES_FIXTURE_SCENARIOS = [
  "populated",
  "empty",
  "single",
  "many",
  "duplicates",
  "unknown",
  "hidden",
  "withdrawn",
  "closed",
  "missing-presentation",
  "loading",
  "partial",
  "stale",
  "unavailable",
  "error",
  "auth-expired",
  "customer-mismatch",
  "pagination",
] as const;

export type CustomerFavouritesFixtureScenario =
  (typeof CUSTOMER_FAVOURITES_FIXTURE_SCENARIOS)[number];

export const DEFAULT_CUSTOMER_FAVOURITES_FIXTURE_SCENARIO =
  "populated" as const satisfies CustomerFavouritesFixtureScenario;

export const CUSTOMER_FAVOURITES_FIXTURE_WRITABLE_CAPABILITIES: CustomerFavouritesCapabilities =
  Object.freeze({
    add: Object.freeze({ status: "allowed" as const }),
    remove: Object.freeze({ status: "allowed" as const }),
  });

export const CUSTOMER_FAVOURITES_FIXTURE_READ_ONLY_CAPABILITIES: CustomerFavouritesCapabilities =
  Object.freeze({
    add: Object.freeze({
      status: "unavailable" as const,
      reason: "provider-disabled" as const,
    }),
    remove: Object.freeze({
      status: "unavailable" as const,
      reason: "provider-disabled" as const,
    }),
  });

function buildResultsFallbackDestination(locationName: string) {
  return `/results?searchScope=locations&search=${encodeURIComponent(locationName)}`;
}

const marketplaceLocationCatalogEntries: readonly CustomerFavouriteLocationCatalogEntry[] =
  VENUE_RESULTS_FIXTURES.map((venue) => ({
    locationId: venue.id,
    displayRevision: CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION,
    businessId: `marketplace-business-${venue.id.slice("venue-".length)}`,
    businessName: venue.name,
    locationName: venue.name,
    publicationState: "public" as const,
    businessStatus: "open" as const,
    bookingStatus: "available" as const,
    images: venue.images.map((image) => ({
      imageId: image.id,
      url: image.src,
      alt: image.alt,
    })),
    rating: venue.rating,
    reviewCount: venue.reviewCount,
    address: venue.address,
    category: venue.category,
    badge: venue.badge
      ? {
          badgeId: `results-${venue.badge.variant ?? "default"}`,
          label: venue.badge.label,
        }
      : null,
    destination: buildLocationProfilePath(venue.id),
  }));

const hardeningLocationCatalogEntries: readonly CustomerFavouriteLocationCatalogEntry[] =
  [
    {
      locationId: "venue-closed-public-fixture",
      displayRevision: CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION,
      businessId: "fixture-business-closed-public",
      businessName: "Quiet Corner",
      locationName: "Quiet Corner",
      publicationState: "public",
      businessStatus: "closed",
      bookingStatus: "unavailable",
      images: [
        {
          imageId: "fixture-image-quiet-corner",
          url: "/images/widget/placeholders/location-placeholder.png",
          alt: "Quiet Corner storefront",
        },
      ],
      rating: 4.4,
      reviewCount: 51,
      address: "Strada Plantelor 20, București",
      category: "Wellness",
      badge: null,
      destination: buildResultsFallbackDestination("Quiet Corner"),
    },
    {
      locationId: "venue-missing-presentation-fixture",
      displayRevision: CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION,
      businessId: "fixture-business-missing-presentation",
      businessName: "Simple Studio",
      locationName: "Simple Studio",
      publicationState: "public",
      businessStatus: "open",
      bookingStatus: "unknown",
      images: [],
      rating: null,
      reviewCount: null,
      address: null,
      category: null,
      badge: null,
      destination: buildResultsFallbackDestination("Simple Studio"),
    },
    {
      locationId: "venue-hidden-fixture",
      displayRevision: CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION,
      businessId: "fixture-business-hidden",
      businessName: "Hidden Studio",
      locationName: "Hidden Studio",
      publicationState: "hidden",
      businessStatus: "open",
      bookingStatus: "unavailable",
      images: [],
      rating: null,
      reviewCount: null,
      address: null,
      category: null,
      badge: null,
      destination: null,
    },
    {
      locationId: "venue-withdrawn-fixture",
      displayRevision: CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION,
      businessId: "fixture-business-withdrawn",
      businessName: "Withdrawn Studio",
      locationName: "Withdrawn Studio",
      publicationState: "withdrawn",
      businessStatus: "closed",
      bookingStatus: "unavailable",
      images: [],
      rating: null,
      reviewCount: null,
      address: null,
      category: null,
      badge: null,
      destination: null,
    },
  ];

const validatedCatalog = validateCustomerFavouriteLocationCatalog({
  schemaVersion: CUSTOMER_FAVOURITES_SCHEMA_VERSION,
  kind: "customer-favourite-location-catalog",
  displayRevision: CUSTOMER_FAVOURITES_FIXTURE_DISPLAY_REVISION,
  locations: [
    ...marketplaceLocationCatalogEntries,
    ...hardeningLocationCatalogEntries,
  ],
});

if (!validatedCatalog) {
  throw new Error("Customer Favourites fixture catalog is invalid.");
}

export const CUSTOMER_FAVOURITES_FIXTURE_LOCATION_CATALOG = validatedCatalog;

export function createCustomerFavouritesFixtureLocationCatalog(): CustomerFavouriteLocationCatalog {
  return structuredClone(CUSTOMER_FAVOURITES_FIXTURE_LOCATION_CATALOG);
}

export const CUSTOMER_FAVOURITES_FIXTURE_MARKETPLACE_LOCATION_IDS =
  Object.freeze(VENUE_RESULTS_FIXTURES.map((venue) => venue.id));

const defaultSavedLocationIds = [
  "venue-glow-room",
  "venue-pure-spa",
  "venue-gymone4spa",
  "venue-3rd-generation",
  "venue-moonlight-nails",
  "venue-blind-barbers",
  "venue-urban-nails",
] as const;

function reference(
  favouriteId: string,
  locationId: string,
  index: number,
): CustomerFavouriteLocationReferenceV1 {
  return {
    favouriteId,
    entityType: "location",
    locationId,
    savedAtEpochMs:
      CUSTOMER_FAVOURITES_FIXTURE_GENERATED_AT_EPOCH_MS - index * 60_000,
  };
}

function referencesForLocationIds(locationIds: readonly string[]) {
  return locationIds.map((locationId, index) =>
    reference(`fixture-favourite-${index + 1}`, locationId, index),
  );
}

export function createCustomerFavouritesFixtureReferences(
  scenario: CustomerFavouritesFixtureScenario,
): readonly CustomerFavouriteLocationReferenceV1[] {
  switch (scenario) {
    case "empty":
      return [];
    case "single":
      return referencesForLocationIds(defaultSavedLocationIds.slice(0, 1));
    case "many":
    case "pagination":
      return referencesForLocationIds(
        CUSTOMER_FAVOURITES_FIXTURE_MARKETPLACE_LOCATION_IDS,
      );
    case "duplicates":
      return [
        reference("fixture-favourite-duplicate-a", "venue-glow-room", 0),
        reference("fixture-favourite-duplicate-b", "venue-glow-room", 1),
        reference("fixture-favourite-duplicate-a", "venue-pure-spa", 2),
        reference("fixture-favourite-duplicate-c", "venue-gymone4spa", 3),
      ];
    case "unknown":
      return [
        reference("fixture-favourite-unknown", "venue-unknown-fixture", 0),
      ];
    case "hidden":
      return [
        reference("fixture-favourite-hidden", "venue-hidden-fixture", 0),
      ];
    case "withdrawn":
      return [
        reference(
          "fixture-favourite-withdrawn",
          "venue-withdrawn-fixture",
          0,
        ),
      ];
    case "closed":
      return [
        reference(
          "fixture-favourite-closed",
          "venue-closed-public-fixture",
          0,
        ),
      ];
    case "missing-presentation":
      return [
        reference(
          "fixture-favourite-missing-presentation",
          "venue-missing-presentation-fixture",
          0,
        ),
      ];
    default:
      return referencesForLocationIds(defaultSavedLocationIds);
  }
}

export function buildCustomerFavouritesFixtureCursor(input: {
  scenario: CustomerFavouritesFixtureScenario;
  locale: CustomerFavouritesLocale;
  snapshotRevision: string;
  offset: number;
  pageSize: number;
}) {
  return [
    "cfav",
    input.scenario,
    input.locale,
    input.snapshotRevision,
    input.offset,
    input.pageSize,
  ].join(".");
}

export function parseCustomerFavouritesFixtureCursor(
  value: string,
  expectation: {
    scenario: CustomerFavouritesFixtureScenario;
    locale: CustomerFavouritesLocale;
    snapshotRevision: string;
    pageSize: number;
    totalCount: number;
  },
) {
  const parts = value.split(".");
  if (
    parts.length !== 6 ||
    parts[0] !== "cfav" ||
    parts[1] !== expectation.scenario ||
    parts[2] !== expectation.locale ||
    parts[3] !== expectation.snapshotRevision ||
    parts[5] !== String(expectation.pageSize)
  ) {
    return null;
  }

  const offset = Number(parts[4]);
  return Number.isSafeInteger(offset) &&
    offset > 0 &&
    offset < expectation.totalCount &&
    offset % expectation.pageSize === 0
    ? offset
    : null;
}

export function createCustomerFavouritesFixtureSnapshot(input: {
  customerAccountId: string;
  scenario: CustomerFavouritesFixtureScenario;
  catalog: CustomerFavouriteLocationCatalog;
  cursor: string | null;
  pageSize: number;
  locale?: CustomerFavouritesLocale;
  snapshotRevision?: string;
  capabilities?: CustomerFavouritesCapabilities;
}): CustomerFavouritesSnapshotV1 | null {
  const snapshotRevision =
    input.snapshotRevision ?? CUSTOMER_FAVOURITES_FIXTURE_SNAPSHOT_REVISION;
  const locale = input.locale ?? CUSTOMER_FAVOURITES_FIXTURE_DEFAULT_LOCALE;
  const allReferences = createCustomerFavouritesFixtureReferences(input.scenario);
  const offset =
    input.cursor === null
      ? 0
      : parseCustomerFavouritesFixtureCursor(input.cursor, {
          scenario: input.scenario,
          locale,
          snapshotRevision,
          pageSize: input.pageSize,
          totalCount: allReferences.length,
        });

  if (offset === null) {
    return null;
  }

  const favourites = allReferences.slice(offset, offset + input.pageSize);
  const nextOffset = offset + favourites.length;
  const nextCursor =
    nextOffset < allReferences.length
      ? buildCustomerFavouritesFixtureCursor({
          scenario: input.scenario,
          locale,
          snapshotRevision,
          offset: nextOffset,
          pageSize: input.pageSize,
        })
      : null;
  const sourceSnapshot = {
    schemaVersion: CUSTOMER_FAVOURITES_SCHEMA_VERSION,
    kind: "customer-favourites-snapshot" as const,
    customerAccountId: input.customerAccountId,
    providerId: CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_ID,
    providerRevision: CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_REVISION,
    snapshotId: `fixture-favourites-${input.scenario}-${locale}-${input.customerAccountId}`,
    snapshotRevision,
    displayRevision: input.catalog.displayRevision,
    generatedAtEpochMs: CUSTOMER_FAVOURITES_FIXTURE_GENERATED_AT_EPOCH_MS,
    capabilities:
      input.capabilities ?? CUSTOMER_FAVOURITES_FIXTURE_WRITABLE_CAPABILITIES,
    favourites,
    items: [],
    rejected: [],
    pagination: {
      cursor: input.cursor,
      nextCursor,
      pageSize: input.pageSize,
      returnedCount: favourites.length,
      totalCount: allReferences.length,
    },
  };
  const joined = joinCustomerFavouriteLocations(sourceSnapshot, input.catalog);
  if (!joined.valid) {
    return null;
  }

  return validateCustomerFavouritesSnapshot({
    ...sourceSnapshot,
    items: joined.items,
    rejected: joined.rejected,
  });
}
